import {CustomerTimelineEntry} from '@phothor/manage/customers/state/customer-timeline-entry';

export interface TimelineDayGroup {
  dayKey: string;
  dayDate: Date;
  events: TimelineBucket[];
}

export interface TimelineBucket {
  key: string;
  label: string;
  description: string;
  icon: string;
  iconClasses: string;
  count: number;
  latestAt: Date;
  entries: CustomerTimelineEntry[];
  expanded?: boolean;
}

export interface TimelineActionMeta {
  label: string;
  description: string;
  icon: string;
  iconClasses: string;
}

export function buildTimelineGroups(entries: CustomerTimelineEntry[]): TimelineDayGroup[] {
  const sorted = [...entries]
    .filter(entry => entry.occurredAtDate)
    .sort((a, b) => {
      const aTime = a.occurredAtDate ? a.occurredAtDate.getTime() : 0;
      const bTime = b.occurredAtDate ? b.occurredAtDate.getTime() : 0;
      return bTime - aTime;
    });

  const dayMap = new Map<string, TimelineDayGroup>();

  sorted.forEach(entry => {
    if (!entry.occurredAtDate) {
      return;
    }
    const dayKey = getLocalDayKey(entry.occurredAtDate);
    let dayGroup = dayMap.get(dayKey);
    if (!dayGroup) {
      const dayDate = new Date(entry.occurredAtDate);
      dayDate.setHours(0, 0, 0, 0);
      dayGroup = {dayKey, dayDate, events: []};
      dayMap.set(dayKey, dayGroup);
    }

    const bucketTime = new Date(entry.occurredAtDate);
    bucketTime.setMinutes(0, 0, 0);
    const runKey = [
      dayKey,
      bucketTime.getTime(),
      entry.actionType,
      entry.galleryId ?? 'g',
      entry.itemId ?? 'i'
    ].join(':');

    let bucket: TimelineBucket | undefined = dayGroup.events[dayGroup.events.length - 1];
    if (!bucket || bucket.key.split('#', 1)[0] !== runKey) {
      bucket = undefined;
    }

    if (!bucket) {
      const meta = getTimelineActionMeta(entry);
      bucket = {
        key: `${runKey}#${dayGroup.events.length}`,
        label: meta.label,
        description: meta.description,
        icon: meta.icon,
        iconClasses: meta.iconClasses,
        count: 0,
        latestAt: entry.occurredAtDate,
        entries: []
      };
      dayGroup.events.push(bucket);
    }

    bucket.count += 1;
    bucket.entries.push(entry);
    if (entry.occurredAtDate > bucket.latestAt) {
      bucket.latestAt = entry.occurredAtDate;
    }
  });

  return Array.from(dayMap.values())
    .sort((a, b) => b.dayDate.getTime() - a.dayDate.getTime())
    .map(group => ({
      ...group,
      events: group.events.sort((a, b) => b.latestAt.getTime() - a.latestAt.getTime())
    }));
}

function getLocalDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getTimelineActionMeta(entry: CustomerTimelineEntry | Partial<CustomerTimelineEntry>): TimelineActionMeta {
  const actionType = entry.actionType || 'ACTIVITY';
  const baseGallery = entry.galleryName || 'A gallery';
  const baseItem = entry.itemFilename || 'an item';

  switch (actionType) {
    case 'VISIT':
      return {
        label: 'Visited gallery',
        description: baseGallery,
        icon: 'lucide--mouse-pointer-2',
        iconClasses: 'bg-primary/10 text-primary border border-primary/10'
      };
    case 'LIKE':
      return {
        label: 'Liked a photo',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--heart',
        iconClasses: 'bg-error/10 text-error border border-error/10'
      };
    case 'UNLIKE':
      return {
        label: 'Removed a like',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--heart-off',
        iconClasses: 'bg-base-200 text-base-content border border-base-200'
      };
    case 'ITEM_FULL_VIEW':
      return {
        label: 'Viewed a photo',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--eye',
        iconClasses: 'bg-warning/10 text-warning border border-warning/10'
      };
    case 'ITEM_CLICK':
      return {
        label: 'Opened a preview',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--square-mouse-pointer',
        iconClasses: 'bg-warning/10 text-warning border border-warning/10'
      };
    case 'ITEM_DOWNLOAD_LINK_ISSUED':
      return {
        label: 'Photo download link prepared',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--download',
        iconClasses: 'bg-secondary/10 text-secondary border border-secondary/10'
      };
    case 'ITEM_DOWNLOAD':
      return {
        label: 'Downloaded photo',
        description: `${baseItem} · ${baseGallery}`,
        icon: 'lucide--download',
        iconClasses: 'bg-secondary/10 text-secondary border border-secondary/10'
      };
    case 'GALLERY_DOWNLOAD_LINK_ISSUED':
      return {
        label: 'Requested gallery download',
        description: baseGallery,
        icon: 'lucide--folder-down',
        iconClasses: 'bg-secondary/10 text-secondary border border-secondary/10'
      };
    case 'GALLERY_DOWNLOAD':
      return {
        label: 'Downloaded gallery ZIP',
        description: baseGallery,
        icon: 'lucide--folder-check',
        iconClasses: 'bg-secondary/10 text-secondary border border-secondary/10'
      };
    case 'PERSONAL_LINK_OPENED':
      return {
        label: 'Opened personal link',
        description: baseGallery,
        icon: 'lucide--link',
        iconClasses: 'bg-primary/10 text-primary border border-primary/10'
      };
    case 'LOGIN':
      return {
        label: 'Signed in',
        description: baseGallery,
        icon: 'lucide--log-in',
        iconClasses: 'bg-info/10 text-info border border-info/10'
      };
    case 'EMAIL_ENTERED':
      return {
        label: 'Entered email',
        description: baseGallery,
        icon: 'lucide--mail',
        iconClasses: 'bg-primary/10 text-primary border border-primary/10'
      };
    default:
      return {
        label: actionType.replaceAll('_', ' ').toLowerCase(),
        description: baseGallery,
        icon: 'lucide--activity',
        iconClasses: 'bg-base-200 text-base-content border border-base-200'
      };
  }
}
